/**
 * Quality surfaces for customer service and sales reps (kanban 2a28d4c0).
 *
 * - My asks: `/api/portal/asks/mine` (+ answer / not-mine), the OPEN asks
 *   Merlin sent this person for the quality desk.
 * - Customer complaints: `/api/portal/customers/quality-summary` (pills on the
 *   Customers table) and `/api/portal/customers/{no}/quality` (Complaints tab).
 * - Report a complaint: `POST /api/portal/quality-complaints` (multipart).
 */
import api from './api';

export interface MyAskCase {
  investigation_no: string;
  title: string;
}

export interface MyAskThread {
  thread_no: string;
  title: string;
}

export interface MyAskCustomer {
  customer_no: string;
  name: string | null;
}

export interface MyAsk {
  id: string;
  question_title: string;
  question_body: string;
  context_summary: string;
  asked_at: string;
  asked_for: string;
  via: 'Merlin';
  case: MyAskCase | null;
  thread: MyAskThread | null;
  customer: MyAskCustomer | null;
  reminder_at: string | null;
  status: 'open';
}

export interface AskActionResult {
  id: string;
  status: string;
  response_id: string;
}

export interface OpenComplaintRef {
  ref: string;
  title: string;
  waits_on: string | null;
  updated_at: string;
}

export interface CompletedComplaintRef {
  ref: string;
  title: string;
  completed_at: string;
}

export interface CustomerQualitySummary {
  customer_no: string;
  open_count: number;
  open: OpenComplaintRef[];
  recently_completed: CompletedComplaintRef | null;
  /** Newest change across the open complaints (a just-filed one counts from
   *  its creation). Absent from older backends. */
  last_update_at?: string | null;
}

export interface CustomerComplaint {
  kind: 'case' | 'thread';
  ref: string;
  title: string;
  status: string;
  is_open: boolean;
  waits_on: string | null;
  updated_at: string;
  completed_at: string | null;
  case_ref: string | null;
}

export interface ReportComplaintInput {
  customer_no: string;
  product: string;
  what_happened: string;
  photos: File[];
}

export interface ReportComplaintResult {
  issue_id: string;
  issue_no: string;
  thread_id: string;
  thread_no: string;
  customer_no: string;
  photo_count: number;
}

export async function getMyAsks(): Promise<MyAsk[]> {
  const { data } = await api.get<MyAsk[]>('/portal/asks/mine');
  return data;
}

export async function answerMyAsk(id: string, bodyText: string): Promise<AskActionResult> {
  const { data } = await api.post<AskActionResult>(`/portal/asks/${id}/answer`, { body_text: bodyText });
  return data;
}

export async function passAskBack(id: string, note: string): Promise<AskActionResult> {
  const { data } = await api.post<AskActionResult>(`/portal/asks/${id}/not-mine`, { note });
  return data;
}

export async function getCustomerQualitySummary(customerNos: string[]): Promise<CustomerQualitySummary[]> {
  if (customerNos.length === 0) return [];
  const { data } = await api.get<CustomerQualitySummary[]>('/portal/customers/quality-summary', {
    params: { customer_nos: customerNos.join(',') },
  });
  return data;
}

export async function getCustomerComplaints(customerNo: string): Promise<CustomerComplaint[]> {
  const { data } = await api.get<CustomerComplaint[]>(
    `/portal/customers/${encodeURIComponent(customerNo)}/quality`,
  );
  return data;
}

export async function reportQualityComplaint(input: ReportComplaintInput): Promise<ReportComplaintResult> {
  const form = new FormData();
  form.append('customer_no', input.customer_no);
  form.append('product', input.product);
  form.append('what_happened', input.what_happened);
  input.photos.forEach((p) => form.append('photos', p));
  const { data } = await api.post<ReportComplaintResult>('/portal/quality-complaints', form, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return data;
}
